import { useState, useEffect, useRef } from "react";
import apiFetch from "@wordpress/api-fetch";

const Video = ({ data, setAttributes }) => {
  const [thumbnail, setThumbnail] = useState("");
  const [videoTitle, setVideoTitle] = useState("Vidpress Video");
  const prevUrl = useRef(null); // Store previous video URL

  const getVideoProvider = (url) => {
    if (!url) return null;
    if (url.includes("youtube.com") || url.includes("youtu.be")) return "youtube";
    if (url.includes("vimeo.com")) return "vimeo";
    return null;
  };

  const getYouTubeId = (url) => {
    return url.split("v=")[1]?.split("&")[0] || url.split("youtu.be/")[1]?.split("?")[0];
  };

  const getVimeoId = (url) => {
    return url.split("vimeo.com/")[1]?.split("?")[0];
  };

  const fetchYouTubeTitle = async (videoId) => {
    try {
      const response = await fetch(`https://noembed.com/embed?url=https://www.youtube.com/watch?v=${videoId}`);
      const data = await response.json();
      return data.title || "Vidpress Video";
    } catch (err) {
      return "Vidpress Video";
    }
  };

  const uploadOrUpdateThumbnail = async (imageUrl, filename, existingThumbnailId) => {
    const response = await fetch(imageUrl);
    const blob = await response.blob();

    const formData = new FormData();
    formData.append('file', blob, filename);
    formData.append('title', filename);

    let uploadResponse;
    if (existingThumbnailId) {
      // Update existing media item
      uploadResponse = await apiFetch({
        path: `/wp/v2/media/${existingThumbnailId}`,
        method: "POST",
        body: formData,
      });
    } else {
      // Upload new media item
      uploadResponse = await apiFetch({
        path: "/wp/v2/media",
        method: "POST",
        body: formData,
      });
    }

    return uploadResponse.id;
  };

  useEffect(() => {
    if (!data.video_url || data.video_url === prevUrl.current) return; // Skip if URL didn't change

    prevUrl.current = data.video_url; // Update previous URL

    const videoProvider = getVideoProvider(data.video_url);
    setAttributes({ video_provider: videoProvider });

    if (!videoProvider) return;

    (async () => {
      let thumbnailUrl = "";
      let title = "Vidpress Video";

      if (videoProvider === "youtube") {
        const videoId = getYouTubeId(data.video_url);
        title = await fetchYouTubeTitle(videoId);
        thumbnailUrl = `https://i.ytimg.com/vi/${videoId}/maxresdefault.jpg`;

        const res = await fetch(thumbnailUrl);
        if (!res.ok) {
          thumbnailUrl = `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`;
        }
      } else if (videoProvider === "vimeo") {
        const videoId = getVimeoId(data.video_url);
        try {
          const response = await fetch(`https://vimeo.com/api/v2/video/${videoId}.json`);
          const vimeoData = await response.json();
          title = vimeoData[0]?.title || "Vidpress Video";
          thumbnailUrl = vimeoData[0]?.thumbnail_large || "";
        } catch (error) {
          console.error("Error fetching Vimeo data", error);
        }
      }

      setThumbnail(thumbnailUrl);
      setVideoTitle(title);

      if (thumbnailUrl) {
        try {
          const mediaId = await uploadOrUpdateThumbnail(
            thumbnailUrl,
            `${title}-${Date.now()}.jpg`,
            data.thumbnail_id
          );

          if (mediaId) {
            console.log("Thumbnail uploaded/updated with Media ID:", mediaId);
            setAttributes({ thumbnail_id: mediaId });
          }
        } catch (error) {
          console.error("Thumbnail upload/update failed:", error);
        }
      }
    })();

  }, [data.video_url]);

  return (
    <div className="vidpress">
      {thumbnail ? (
        <>
          <img className="vidpress__thumbnail" src={thumbnail} alt="Video Thumbnail" />
          <div className="vidpress__controls">
            <div className="vidpress__controls-group">
              <button className="vidpress__controls-btn">
                <span className="dashicons dashicons-controls-back"></span>
              </button>
              <button className="vidpress__controls-btn vidpress__controls-btn--play">
                <span className="dashicons dashicons-controls-play"></span>
              </button>
              <button className="vidpress__controls-btn">
                <span className="dashicons dashicons-controls-forward"></span>
              </button>
            </div>

            <div className="vidpress__progress">
              <input type="range" className="vidpress__progress-bar" value="0" min="0" max="100" step="0.1" />
            </div>

            <div className="vidpress__volume">
              <button className="vidpress__controls-btn" id="muteButton">
                <span className="dashicons dashicons-controls-volumeon"></span>
              </button>
              <input type="range" className="vidpress__volume-slider" value="80" min="0" max="100" step="1" />
            </div>
          </div>
        </>
      ) : (
        data.video_provider !== 'youtube' &&
        data.video_provider !== 'vimeo' && (
          <div className="vidpress__error">Unsupported video URL</div>
        )
      )}
    </div>
  );
};

export default Video;
